import React, { useEffect } from 'react';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar'
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Link from '@mui/material/Link';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { MainListItems, SecondaryListItems } from './../../components/dashboard/listItems';
import Chart from './../../components/dashboard/Chart';
import Deposits from './../../components/dashboard/Deposits';
import Orders from './../../components/dashboard/Orders';
import logo from './../../assets/logo/logo_black.svg'

import { StreamApp, StatusUpdateForm, FlatFeed, NotificationDropdown, Notification } from 'react-activity-feed';
import 'react-activity-feed/dist/index.css';

import routes from '../../routes';

import { useAppDispatch, useAppSelector } from '../../hooks';
import authAccount, { cognitoSignOut, reloadAuthAccount } from '../../managers/redux/slices/authAccount';
import ProfileAvatar from '../../components/data-display/ProfileAvatar';
import { Route, Switch, useHistory } from 'react-router-dom';
import Subscription from '../admin/Subscription';
import SelectStaffProfile from '../admin/SelectStaffProfile';
import OrganizationRegistration from '../admin/OrganizationRegistration';
import { ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import DevRevSupport from '../../components/DevRevSupport';

function Copyright(props: any) {
    return (
        <Typography variant="body2" color="text.secondary" align="center" {...props}>
            {'Copyright © '}
            <Link color="inherit" href="https://mui.com/">
                Your Website
            </Link>{' '}
            {new Date().getFullYear()}
            {'.'}
        </Typography>
    );
}

const drawerWidth: number = 240;

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        '& .MuiDrawer-paper': {
            position: 'relative',
            whiteSpace: 'nowrap',
            width: drawerWidth,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
            boxSizing: 'border-box',
            ...(!open && {
                overflowX: 'hidden',
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                }),
                width: theme.spacing(7),
                [theme.breakpoints.up('sm')]: {
                    width: theme.spacing(9),
                },
            }),
        },
    }),
);

const mdTheme = createTheme();

export default function AdminLayout(props: any) {

    const dispatch = useAppDispatch()
    const history = useHistory()

    const [open, setOpen] = React.useState(true);
    const toggleDrawer = () => {
        setOpen(!open);
    };

    const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);
    const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);

    const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElNav(event.currentTarget);
    };
    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const getRoutes = (routes: any) => {
        return routes.map((prop: any, key: any) => {
            if (prop.collapse) {
                return getRoutes(prop.views);
            }
            if (prop.layout === "/admin") {
                return (
                    <Route
                        path={prop.layout + prop.path}
                        component={prop.component}
                        key={key}
                        exact={props.exact}
                    />
                );
            } else {
                return null;
            }
        });
    };

    const authAccount = useAppSelector(state => state.authAccount.data)
    const selectedStaffProfile = useAppSelector(state => state.authAccount.selectedStaffProfile)
    const communicationsProfile = useAppSelector(state => state.authAccount.communicationsProfile)

    useEffect(() => {
        if (authAccount == null) {
            dispatch(reloadAuthAccount())
        }
    }, [authAccount])


    if (selectedStaffProfile && selectedStaffProfile.organization && selectedStaffProfile.organization.latentSubscription && selectedStaffProfile.organization.latentSubscription > 0) {
        return (
            <Box sx={{ display: 'flex', zIndex: 1 }}>
                <CssBaseline />
                <AppBar position="absolute" sx={{ zIndex: 1 }} open={open}>
                    <Toolbar
                        sx={{
                            pr: '24px', // keep right padding when drawer closed
                        }}
                    >
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="open drawer"
                            onClick={toggleDrawer}
                            sx={{
                                marginRight: '36px',
                                ...(open && { display: 'none' }),
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography
                            component="h1"
                            variant="h6"
                            color="inherit"
                            noWrap
                            sx={{ flexGrow: 1 }}
                        >

                        </Typography>
                        {/* {
                            communicationsProfile && communicationsProfile.streamJwt &&
                            <StreamApp
                                apiKey="ax9qpj459r9x"
                                appId="1170890"
                                token={communicationsProfile && communicationsProfile.streamJwt ? communicationsProfile.streamJwt : ''}
                            >
                                <NotificationDropdown feedGroup="notification" style={{ color: 'white', backgroundColor: 'white', padding: 5, borderRadius: 20, height: 40, width: 40, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }} userId={communicationsProfile.id} notify
                                    Group={(group) => { console.log(group); return(<div></div>) }}
                                />
                            </StreamApp>
                        } */}
                        <Typography
                            variant="h4"
                            color="inherit"
                            className="mx-3"
                        >
                            {selectedStaffProfile && selectedStaffProfile.organization ? selectedStaffProfile.organization.shortName ? selectedStaffProfile.organization.shortName : selectedStaffProfile.organization.name ? selectedStaffProfile.organization.name : '' : ''}
                        </Typography>
                        <Tooltip title="Open settings">
                            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                                <ProfileAvatar authAccountID={authAccount ? authAccount.id : null} />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            sx={{ mt: '45px' }}
                            id="menu-appbar"
                            anchorEl={anchorElUser}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorElUser)}
                            onClose={handleCloseUserMenu}
                            disableAutoFocus
                        >
                            {
                                selectedStaffProfile &&
                                <MenuItem onClick={() => { history.push('/admin/organization'); handleCloseUserMenu() }}>
                                    <ProfileAvatar organizationID={selectedStaffProfile ? selectedStaffProfile.organizationID : null} size="small" /> <Typography variant="body1" className="mx-2">{selectedStaffProfile && selectedStaffProfile.organization ? selectedStaffProfile.organization.shortName ? selectedStaffProfile.organization.shortName : selectedStaffProfile.organization.name ? selectedStaffProfile.organization.name : '' : ''}</Typography>
                                </MenuItem>
                            }
                            {
                                authAccount &&
                                <MenuItem onClick={() => { history.push('/admin/profile'); handleCloseUserMenu() }}>
                                    <ProfileAvatar authAccountID={authAccount ? authAccount.id : null} /> <Typography variant="body1" className="mx-2">{authAccount && authAccount.name ? authAccount.name : authAccount && authAccount.firstName && authAccount.lastName ? `${authAccount.firstName} ${authAccount.lastName}` : 'My Account'}</Typography>
                                </MenuItem>
                            }
                            <Divider />
                            <MenuItem key={'signOut'} onClick={() => {
                                console.log('signing out');
                                dispatch(cognitoSignOut())
                                handleCloseUserMenu()
                            }}>
                                <Typography textAlign="center">Sign Out</Typography>
                            </MenuItem>
                            {
                                authAccount && authAccount.staffProfile && authAccount.staffProfile.items.length > 1 &&
                                <MenuItem key={'switchOrganization'} onClick={() => {
                                    history.push('/admin/select-organization')
                                    handleCloseUserMenu()
                                }}>
                                    <Typography textAlign="center">Switch Organization</Typography>
                                </MenuItem>
                            }
                        </Menu>
                    </Toolbar>
                </AppBar>
                <Drawer variant="permanent" sx={{ zIndex: 1 }} open={open}>
                    <Toolbar
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: [1],
                        }}
                    >
                        {
                            open &&
                            <div className="d-flex flex-row justify-content-center align-items-center">
                                <img src={logo} style={{ height: 40, color: 'black' }} />
                                <p style={{ fontSize: 36, fontFamily: 'Gilroy-Bold', color: 'black', padding: 0, margin: 0, marginLeft: 10 }}>Latent</p>
                            </div>
                        }
                        <IconButton onClick={toggleDrawer}>
                            {open ? <ChevronLeftIcon /> : <MenuIcon />}
                        </IconButton>
                    </Toolbar>
                    <Divider />
                    <List component="nav">
                        <MainListItems />
                        <Divider sx={{ my: 1 }} />
                        <SecondaryListItems />
                    </List>
                </Drawer>
                <Box
                    component="main"
                    sx={{
                        backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                                ? theme.palette.grey[100]
                                : theme.palette.grey[900],
                        flexGrow: 1,
                        height: '100vh',
                        overflow: 'auto',
                    }}
                    className="mt-5"
                >
                    <Switch>
                        {getRoutes(routes)}
                    </Switch>
                </Box>
                <DevRevSupport />
            </Box>
        );
    } else {
        return (
            <Box sx={{ display: 'flex', zIndex: 1 }}>
                <CssBaseline />
                <AppBar position="absolute" sx={{ zIndex: 1 }} open={open}>
                    <Toolbar
                        sx={{
                            pr: '24px', // keep right padding when drawer closed
                        }}
                    >
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="open drawer"
                            onClick={toggleDrawer}
                            sx={{
                                marginRight: '36px',
                                ...(open && { display: 'none' }),
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography
                            component="h1"
                            variant="h6"
                            color="inherit"
                            noWrap
                            sx={{ flexGrow: 1 }}
                        >

                        </Typography>
                        {/* {
                            communicationsProfile && communicationsProfile.streamJwt &&
                            <StreamApp
                                apiKey="ax9qpj459r9x"
                                appId="1170890"
                                token={communicationsProfile && communicationsProfile.streamJwt ? communicationsProfile.streamJwt : ''}
                            >
                                <NotificationDropdown feedGroup="notification" style={{ color: 'white', backgroundColor: 'white', padding: 5, borderRadius: 20, height: 40, width: 40, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }} userId={communicationsProfile.id} notify />
                            </StreamApp>
                        } */}
                        <Typography
                            variant="h4"
                            color="inherit"
                            className="mx-3"
                        >
                            {selectedStaffProfile && selectedStaffProfile.organization ? selectedStaffProfile.organization.shortName ? selectedStaffProfile.organization.shortName : selectedStaffProfile.organization.name ? selectedStaffProfile.organization.name : '' : ''}
                        </Typography>
                        <Tooltip title="Open settings">
                            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                                <ProfileAvatar authAccountID={authAccount ? authAccount.id : null} />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            sx={{ mt: '45px' }}
                            id="menu-appbar"
                            anchorEl={anchorElUser}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorElUser)}
                            onClose={handleCloseUserMenu}
                            disableAutoFocus
                        >
                            {
                                selectedStaffProfile &&
                                <MenuItem onClick={() => { history.push('/admin/organization') }}>
                                    <ProfileAvatar organizationID={selectedStaffProfile ? selectedStaffProfile.organizationID : null} /> <Typography variant="body1" className="mx-2">{selectedStaffProfile && selectedStaffProfile.organization ? selectedStaffProfile.organization.shortName ? selectedStaffProfile.organization.shortName : selectedStaffProfile.organization.name ? selectedStaffProfile.organization.name : '' : ''}</Typography>
                                </MenuItem>
                            }
                            {
                                authAccount &&
                                <MenuItem onClick={() => { history.push('/admin/profile') }}>
                                    <ProfileAvatar authAccountID={authAccount ? authAccount.id : null} /> <Typography variant="body1" className="mx-2">{authAccount && authAccount.name ? authAccount.name : authAccount && authAccount.firstName && authAccount.lastName ? `${authAccount.firstName} ${authAccount.lastName}` : 'My Account'}</Typography>
                                </MenuItem>
                            }
                            <Divider />
                            <MenuItem key={'signOut'} onClick={() => {
                                console.log('signing out');
                                dispatch(cognitoSignOut())
                            }}>
                                <Typography textAlign="center">Sign Out</Typography>
                            </MenuItem>
                            {
                                authAccount && authAccount.staffProfile && authAccount.staffProfile.items.length > 1 &&
                                <MenuItem key={'switchOrganization'} onClick={() => {
                                    history.push('/admin/select-organization')
                                }}>
                                    <Typography textAlign="center">Switch Organization</Typography>
                                </MenuItem>
                            }
                        </Menu>
                    </Toolbar>
                </AppBar>
                <Drawer variant="permanent" sx={{ zIndex: 1 }} open={open}>
                    <Toolbar
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: [1],
                        }}
                    >
                        {
                            open &&
                            <div className="d-flex flex-row justify-content-center align-items-center">
                                <img src={logo} style={{ height: 40, color: 'black' }} />
                                <p style={{ fontSize: 36, fontFamily: 'Gilroy-Bold', color: 'black', padding: 0, margin: 0, marginLeft: 10 }}>Latent</p>
                            </div>
                        }
                        <IconButton onClick={toggleDrawer}>
                            <ChevronLeftIcon />
                        </IconButton>
                    </Toolbar>
                    <List>
                        <ListItemButton className="h-auto" onClick={() => history.push('/admin/organization-registration')}>
                            <ListItemIcon>
                                <AddBusinessIcon />
                            </ListItemIcon>
                            <ListItemText primary="Registration " />
                        </ListItemButton>
                        <ListItemButton className="h-auto" onClick={() => history.push('/admin/subscription')}>
                            <ListItemIcon>
                                <CreditCardIcon />
                            </ListItemIcon>
                            <ListItemText primary="Subscription " />
                        </ListItemButton>
                    </List>
                    {/* <Divider />
                    <List component="nav">
                        <SecondaryListItems />
                    </List> */}
                </Drawer>
                <Box
                    component="main"
                    sx={{
                        backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                                ? theme.palette.grey[100]
                                : theme.palette.grey[900],
                        flexGrow: 1,
                        minHeight: '100vh',
                        overflow: 'auto',
                    }}
                    className="mt-5"
                >
                    <Switch>
                        <Route path="/admin/dashboard" component={() => (
                            <div>
                                <Typography variant="h3" className="m-5">Select a subscription plan</Typography>
                                <Typography className="m-5 mt-0">In order to start using Latent, you'll need to select a subscription plan. Contact our sales team if you have any questions or want to see a demo.</Typography>
                            </div>
                        )}>
                        </Route>
                        <Route path="/admin/subscription" component={() => (<Subscription />)} />
                        <Route path="/admin/select-organization" component={() => (<SelectStaffProfile />)} />
                        <Route path="/admin/organization-registration" component={() => (<OrganizationRegistration />)} />
                    </Switch>
                </Box>
            </Box>
        );
    }
}